import { useEffect, useMemo, useRef, useState } from "react";
import { line as d3line, area as d3area, curveMonotoneX } from "d3";
import { useParams } from "react-router";
import { InputNumber, Tooltip } from "antd";
import {
  InfoCircleOutlined,
  WarningOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import { useTrialPlans } from "@/hooks/useTrialPlans";
import { usePlannedSites } from "@/hooks/usePlannedSites";
import { activeSites, siteLabel } from "./plannedSites";
import type { PlannedSite, EnrollmentScenario } from "./types";

// --- Projection engine -------------------------------------------------------

interface SiteParams {
  activationOffsetWeeks: number;
  screenedPerMonth: number;
  screenFailureRate: number; // 0–100
  patientPoolCapacity: number;
}

interface MonthPoint {
  month: number;
  cumulative: number;
}

function projectEnrollment(
  sites: SiteParams[],
  target: number,
  rateMultiplier: number,
  lagMultiplier: number,
  rampUpMonths: number,
): MonthPoint[] {
  if (sites.length === 0 || target <= 0) return [];
  const siteCums = new Array(sites.length).fill(0);
  const result: MonthPoint[] = [];

  for (let m = 1; m <= 72; m++) {
    let monthTotal = 0;
    sites.forEach((s, i) => {
      const lagMonths = (s.activationOffsetWeeks / 4.33) * lagMultiplier;
      if (m <= lagMonths) return;
      const monthsActive = m - lagMonths;
      const ramp =
        rampUpMonths > 0 ? Math.min(monthsActive / rampUpMonths, 1) : 1;
      const enrollFraction = 1 - s.screenFailureRate / 100;
      const rate = s.screenedPerMonth * enrollFraction * rateMultiplier * ramp;
      const cap =
        s.patientPoolCapacity > 0
          ? s.patientPoolCapacity - siteCums[i]
          : Infinity;
      const contrib = Math.min(rate, cap);
      siteCums[i] += contrib;
      monthTotal += contrib;
    });
    const prev = result.at(-1)?.cumulative ?? 0;
    const cum = Math.min(prev + monthTotal, target);
    result.push({ month: m, cumulative: Math.round(cum) });
    if (cum >= target) break;
  }
  return result;
}

function completionMonth(points: MonthPoint[], target: number): number | null {
  const pt = points.find((p) => p.cumulative >= target);
  return pt?.month ?? null;
}

function addMonths(dateStr: string, months: number): string {
  const d = new Date(dateStr);
  d.setMonth(d.getMonth() + months);
  return d.toLocaleDateString("en-US", { month: "short", year: "numeric" });
}

// --- Chart -------------------------------------------------------------------

// Vivo chart palette (Mixed set from palletes.ts)
const CHART_COLORS = [
  {
    stroke: "#4596CB",
    bg: "bg-[#D8EEF8]",
    text: "text-[#196A9F]",
    border: "border-[#7ABBDD]",
    hex: "#4596CB",
  },
  {
    stroke: "#6F918E",
    bg: "bg-[#CFE5D7]",
    text: "text-[#486563]",
    border: "border-[#8CBF9C]",
    hex: "#6F918E",
  },
  {
    stroke: "#8C2760",
    bg: "bg-[#E7D2DF]",
    text: "text-[#681042]",
    border: "border-[#B7799F]",
    hex: "#8C2760",
  },
];

interface TooltipState {
  x: number;
  y: number;
  month: number;
  values: { name: string; cumulative: number; color: string }[];
}

interface EnrollmentChartProps {
  results: ScenarioResult[];
  target: number;
  milestoneMonth: number | null;
  fpiDate: string | null;
}

function niceYTicks(target: number): { ticks: number[]; domainMax: number } {
  if (target <= 0) return { ticks: [], domainMax: 1 };
  // Compute a step size that produces 4-6 nice round ticks
  const rawStep = target / 5;
  const mag = Math.pow(10, Math.floor(Math.log10(rawStep)));
  const norm = rawStep / mag;
  const step =
    norm <= 1 ? mag : norm <= 2 ? 2 * mag : norm <= 5 ? 5 * mag : 10 * mag;
  // Build ticks until we've reached or passed target
  const ticks: number[] = [];
  for (let v = step; ; v += step) {
    ticks.push(Math.round(v));
    if (v >= target) break;
  }
  const domainMax = ticks.at(-1) ?? target;
  return { ticks, domainMax };
}

function EnrollmentChart({
  results,
  target,
  milestoneMonth,
  fpiDate,
}: EnrollmentChartProps) {
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [tooltip, setTooltip] = useState<TooltipState | null>(null);
  const [dims, setDims] = useState({ width: 480, height: 300 });

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      if (width > 0 && height > 0) setDims({ width, height });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const VW = dims.width,
    VH = dims.height;
  const ML = 48,
    MR = 16,
    MT = 12,
    MB = 32;
  const iW = VW - ML - MR;
  const iH = VH - MT - MB;

  // X domain: end just past the last scenario completion (or last data point), plus a small buffer
  const completionMonths = results
    .map((r) => r.completionMonths)
    .filter((m): m is number => m != null);
  const allPoints = results.flatMap((r) => r.points);
  const dataEnd =
    completionMonths.length > 0
      ? Math.max(...completionMonths)
      : Math.max(...allPoints.map((p) => p.month), 12);
  const windowEnd = Math.max(dataEnd, milestoneMonth ?? 0);
  const maxMonth = windowEnd + Math.max(Math.ceil(windowEnd * 0.08), 2);

  const { ticks: yTicks, domainMax } = niceYTicks(target);
  // 5% headroom above the top tick so curves don't clip the top border
  const yDomainMax = domainMax * 1.05;

  const xS = (m: number) => ML + (m / maxMonth) * iW;
  const yS = (v: number) => MT + iH - (v / yDomainMax) * iH;

  // Build a point set that includes origin, data, and a plateau extension to chart edge
  const buildPts = (points: MonthPoint[]): MonthPoint[] => {
    const pts: MonthPoint[] = [{ month: 0, cumulative: 0 }, ...points];
    const last = pts.at(-1);
    if (last && last.cumulative >= target) {
      // Extend horizontally at target to the right edge of the chart
      pts.push({ month: maxMonth, cumulative: target });
    }
    return pts;
  };

  const curveLinePath = d3line<MonthPoint>()
    .x((p) => xS(p.month))
    .y((p) => yS(p.cumulative))
    .curve(curveMonotoneX);

  const curveAreaPath = d3area<MonthPoint>()
    .x((p) => xS(p.month))
    .y0(MT + iH)
    .y1((p) => yS(p.cumulative))
    .curve(curveMonotoneX);

  // X-axis tick interval
  const xInterval =
    maxMonth <= 12 ? 2 : maxMonth <= 24 ? 3 : maxMonth <= 36 ? 6 : 12;
  const xTicks: number[] = [];
  for (let m = xInterval; m <= maxMonth; m += xInterval) xTicks.push(m);

  const handleMouseMove = (e: React.MouseEvent<SVGSVGElement>) => {
    if (!svgRef.current) return;
    const rect = svgRef.current.getBoundingClientRect();
    const scaleX = VW / rect.width;
    const px = (e.clientX - rect.left) * scaleX;
    const month = Math.max(0, Math.round(((px - ML) / iW) * maxMonth));
    if (month < 0 || month > maxMonth) {
      setTooltip(null);
      return;
    }

    const values = results.map((r, idx) => {
      const pt = [...r.points].reverse().find((p) => p.month <= month) ?? null;
      return {
        name: r.scenario.name,
        cumulative: pt?.cumulative ?? 0,
        color: CHART_COLORS[idx % CHART_COLORS.length].hex,
      };
    });

    setTooltip({ x: xS(month), y: e.clientY - rect.top, month, values });
  };

  const fmtCount = (n: number) =>
    n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(n);

  return (
    <div
      ref={containerRef}
      className="relative h-full"
      onMouseLeave={() => setTooltip(null)}
    >
      <svg
        ref={svgRef}
        viewBox={`0 0 ${VW} ${VH}`}
        className="w-full h-full"
        onMouseMove={handleMouseMove}
      >
        {/* Grid lines */}
        {yTicks.map((v) => (
          <line
            key={v}
            x1={ML}
            x2={ML + iW}
            y1={yS(v)}
            y2={yS(v)}
            stroke="#F0F2F4"
            strokeWidth={v === target ? 1 : 0.5}
            strokeDasharray={v === target ? "4 3" : undefined}
          />
        ))}
        {xTicks.map((m) => (
          <line
            key={m}
            x1={xS(m)}
            x2={xS(m)}
            y1={MT}
            y2={MT + iH}
            stroke="#F0F2F4"
            strokeWidth={0.5}
          />
        ))}

        {/* Milestone target vertical line */}
        {milestoneMonth != null && milestoneMonth <= maxMonth && (
          <>
            <line
              x1={xS(milestoneMonth)}
              x2={xS(milestoneMonth)}
              y1={MT}
              y2={MT + iH}
              stroke="#F59E0B"
              strokeWidth={1}
              strokeDasharray="4 3"
            />
            <text
              x={xS(milestoneMonth) + 3}
              y={MT + 10}
              fontSize={11}
              fill="#92400E"
              fontWeight={600}
            >
              Target
            </text>
          </>
        )}

        {/* Axes */}
        <line
          x1={ML}
          x2={ML + iW}
          y1={MT + iH}
          y2={MT + iH}
          stroke="#E5E7EB"
          strokeWidth={0.5}
        />
        <line
          x1={ML}
          x2={ML}
          y1={MT}
          y2={MT + iH}
          stroke="#E5E7EB"
          strokeWidth={0.5}
        />

        {/* Y-axis labels */}
        {yTicks.map((v) => (
          <text
            key={v}
            x={ML - 4}
            y={yS(v) + 3.5}
            textAnchor="end"
            fontSize={12}
            fill="#6B7280"
          >
            {fmtCount(v)}
          </text>
        ))}

        {/* X-axis labels */}
        {xTicks.map((m) => (
          <text
            key={m}
            x={xS(m)}
            y={MT + iH + 14}
            textAnchor="middle"
            fontSize={12}
            fill="#6B7280"
          >
            {fpiDate ? addMonths(fpiDate, m) : `Mo ${m}`}
          </text>
        ))}

        {/* Scenario area fills (behind lines) */}
        {results.map((r, idx) => {
          const col = CHART_COLORS[idx % CHART_COLORS.length];
          if (r.points.length === 0) return null;
          const pts = buildPts(r.points);
          const d = curveAreaPath(pts);
          if (!d) return null;
          return (
            <path
              key={`area-${r.scenario.id}`}
              d={d}
              fill={col.stroke}
              fillOpacity={0.08}
              stroke="none"
            />
          );
        })}

        {/* Scenario curves */}
        {results.map((r, idx) => {
          const col = CHART_COLORS[idx % CHART_COLORS.length];
          if (r.points.length === 0) return null;
          const pts = buildPts(r.points);
          const d = curveLinePath(pts);
          if (!d) return null;
          return (
            <path
              key={r.scenario.id}
              d={d}
              fill="none"
              stroke={col.stroke}
              strokeWidth={1.5}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          );
        })}

        {/* Completion markers */}
        {results.map((r, idx) => {
          const col = CHART_COLORS[idx % CHART_COLORS.length];
          const last = r.points.at(-1);
          if (!last || last.cumulative < target) return null;
          return (
            <circle
              key={r.scenario.id}
              cx={xS(last.month)}
              cy={yS(last.cumulative)}
              r={5}
              fill={col.stroke}
              stroke="white"
              strokeWidth={1.5}
            />
          );
        })}

        {/* Hover line */}
        {tooltip && (
          <line
            x1={tooltip.x}
            x2={tooltip.x}
            y1={MT}
            y2={MT + iH}
            stroke="#9CA3AF"
            strokeWidth={1}
            strokeDasharray="3 2"
          />
        )}

        {/* Hover dots on each curve */}
        {tooltip &&
          results.map((r, idx) => {
            const col = CHART_COLORS[idx % CHART_COLORS.length];
            const pt = [...r.points]
              .reverse()
              .find((p) => p.month <= tooltip.month);
            if (!pt) return null;
            return (
              <circle
                key={r.scenario.id}
                cx={xS(tooltip.month)}
                cy={yS(pt.cumulative)}
                r={4}
                fill={col.stroke}
                stroke="white"
                strokeWidth={1.5}
              />
            );
          })}
      </svg>

      {/* Tooltip */}
      {tooltip && (
        <div
          className="absolute pointer-events-none z-10 bg-white border border-[#E5E7EB] rounded-[8px] shadow-lg px-3 py-2.5 min-w-[150px]"
          style={{
            left:
              tooltip.x / VW > 0.62
                ? "auto"
                : `calc(${(tooltip.x / VW) * 100}% + 12px)`,
            right: tooltip.x / VW > 0.62 ? "8px" : "auto",
            top: 20,
          }}
        >
          <p className="text-[12px] text-[#6B7280] font-semibold mb-1.5 m-0 pb-1.5 border-b border-[#F0F2F4]">
            {fpiDate
              ? addMonths(fpiDate, tooltip.month)
              : `Month ${tooltip.month}`}
          </p>
          <div className="flex flex-col gap-1">
            {tooltip.values.map((v) => (
              <div
                key={v.name}
                className="flex items-center justify-between gap-4"
              >
                <div className="flex items-center gap-1.5">
                  <div
                    className="w-2 h-2 rounded-full shrink-0"
                    style={{ background: v.color }}
                  />
                  <span className="text-[12px] text-[#6B7280]">{v.name}</span>
                </div>
                <span className="text-[12px] font-semibold tabular-nums text-[#1F2933]">
                  {v.cumulative}{" "}
                  <span className="text-[12px] font-normal text-[#6B7280]">
                    pts
                  </span>
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// --- Scenario multiplier constants -------------------------------------------

const DEFAULT_SCENARIOS: Omit<EnrollmentScenario, "id">[] = [
  { name: "Optimistic", rateMultiplier: 1.25, activationLagMultiplier: 0.75 },
  { name: "Base Case", rateMultiplier: 1.0, activationLagMultiplier: 1.0 },
  { name: "Pessimistic", rateMultiplier: 0.7, activationLagMultiplier: 1.4 },
];

interface ScenarioResult {
  scenario: EnrollmentScenario;
  points: MonthPoint[];
  completionMonths: number | null;
  projectedDate: string | null;
  isLate: boolean | null;
}

// --- Site parameter row ------------------------------------------------------

function SiteParamRow({
  site,
  onUpdate,
}: {
  site: PlannedSite;
  onUpdate: (changes: Partial<PlannedSite>) => void;
}) {
  return (
    <tr className="border-b border-[#F0F2F4] hover:bg-[#FAFAFA]">
      <td className="px-3 py-2">
        <div>
          <p
            className={`text-[13px] m-0 leading-tight ${
              site.site_name
                ? "font-medium text-[#1F2933]"
                : "italic text-[#6B7280]"
            }`}
          >
            {siteLabel(site)}
          </p>
          {site.investigator_name && (
            <p className="text-[12px] text-[#6B7280] m-0 leading-tight">
              {site.investigator_name}
            </p>
          )}
        </div>
      </td>
      <td className="px-3 py-2 text-[12px] text-[#6B7280]">
        {site.city}, {site.country}
      </td>
      <td className="px-3 py-2">
        <InputNumber
          size="small"
          min={0}
          max={520}
          value={site.activationOffsetWeeks || undefined}
          placeholder="0"
          addonAfter="wk"
          onChange={(v) => onUpdate({ activationOffsetWeeks: v ?? 0 })}
          style={{ width: 100 }}
        />
      </td>
      <td className="px-3 py-2">
        <InputNumber
          size="small"
          min={0}
          max={100}
          step={0.5}
          precision={1}
          value={site.screenedPerMonth || undefined}
          placeholder="0"
          onChange={(v) => onUpdate({ screenedPerMonth: v ?? 0 })}
          style={{ width: 80 }}
        />
      </td>
      <td className="px-3 py-2">
        <InputNumber
          size="small"
          min={0}
          max={99}
          step={1}
          precision={0}
          value={site.screenFailureRate ?? undefined}
          placeholder="—"
          addonAfter="%"
          onChange={(v) =>
            onUpdate({ screenFailureRate: v != null ? v : undefined })
          }
          style={{ width: 90 }}
        />
      </td>
      <td className="px-3 py-2">
        <InputNumber
          size="small"
          min={0}
          value={site.patientPoolCapacity || undefined}
          placeholder="∞"
          onChange={(v) => onUpdate({ patientPoolCapacity: v ?? 0 })}
          style={{ width: 80 }}
        />
      </td>
    </tr>
  );
}

// --- Main component ----------------------------------------------------------

export default function TrialPlanningEnrollment() {
  const { planId } = useParams<{ planId: string }>();
  const { getPlan, updatePlan } = useTrialPlans();
  const plan = planId ? getPlan(planId) : undefined;

  // Derived values computed before early return so all hooks are unconditional
  const targetEnrollment = plan?.targetEnrollment ?? 0;
  const defaultScreenFailureRate = plan?.screenFailureRate ?? 20;
  const rampUpMonths = plan?.rampUpMonths ?? 2;

  const fpiMilestone = plan?.milestones.find(
    (m) => m.event_name === "First Patient In",
  );
  const enrollCompleteMilestone = plan?.milestones.find(
    (m) => m.event_name === "Enrollment Complete",
  );
  const milestoneTarget = enrollCompleteMilestone?.start_date
    ? new Date(enrollCompleteMilestone.start_date)
    : null;

  // Excluded (rejected) sites stay on the plan but sit out the forecast.
  const planSites = useMemo(
    () => activeSites(plan?.sites ?? []),
    [plan?.sites],
  );
  // Same line items, named after the site each chosen investigator works at.
  const displaySites = usePlannedSites(planSites);

  // Total screens needed: sum across sites using per-site SFR where set
  const patientsToScreen = useMemo(() => {
    if (targetEnrollment <= 0) return 0;
    if (planSites.length === 0) {
      return Math.ceil(targetEnrollment / (1 - defaultScreenFailureRate / 100));
    }
    const totalEnrolledRate = planSites.reduce((sum, s) => {
      const sfr = s.screenFailureRate ?? defaultScreenFailureRate;
      const screened = s.screenedPerMonth ?? 0;
      return sum + screened * (1 - sfr / 100);
    }, 0);
    const totalScreenedRate = planSites.reduce(
      (sum, s) => sum + (s.screenedPerMonth ?? 0),
      0,
    );
    if (totalEnrolledRate <= 0 || totalScreenedRate <= 0) {
      return Math.ceil(targetEnrollment / (1 - defaultScreenFailureRate / 100));
    }
    const blendedSfr = 1 - totalEnrolledRate / totalScreenedRate;
    return Math.ceil(targetEnrollment / (1 - blendedSfr));
  }, [planSites, targetEnrollment, defaultScreenFailureRate]);

  const siteParams = planSites.map((s) => ({
    activationOffsetWeeks: s.activationOffsetWeeks ?? 0,
    screenedPerMonth: s.screenedPerMonth ?? 0,
    screenFailureRate: s.screenFailureRate ?? defaultScreenFailureRate,
    patientPoolCapacity: s.patientPoolCapacity ?? 0,
  }));

  const planEnrollmentScenarios = plan?.enrollmentScenarios;
  const scenarios: EnrollmentScenario[] = planEnrollmentScenarios?.length
    ? planEnrollmentScenarios
    : DEFAULT_SCENARIOS.map((s) => ({ ...s, id: crypto.randomUUID() }));

  const results: ScenarioResult[] = useMemo(
    () =>
      scenarios.map((sc) => {
        const points = projectEnrollment(
          siteParams,
          targetEnrollment,
          sc.rateMultiplier,
          sc.activationLagMultiplier,
          rampUpMonths,
        );
        const comp = completionMonth(points, targetEnrollment);
        const projectedDate =
          comp != null && fpiMilestone?.start_date
            ? addMonths(fpiMilestone.start_date, comp)
            : null;
        let isLate: boolean | null = null;
        if (comp != null && fpiMilestone?.start_date && milestoneTarget) {
          const projDate = new Date(fpiMilestone.start_date);
          projDate.setMonth(projDate.getMonth() + comp);
          isLate = projDate > milestoneTarget;
        }
        return {
          scenario: sc,
          points,
          completionMonths: comp,
          projectedDate,
          isLate,
        };
      }),
    // Keyed on the raw inputs rather than the derived scenarios/siteParams.
    // eslint-disable-next-line react-hooks/exhaustive-deps, @eslint-react/exhaustive-deps
    [
      planSites,
      planEnrollmentScenarios,
      targetEnrollment,
      defaultScreenFailureRate,
      rampUpMonths,
      fpiMilestone?.start_date,
      milestoneTarget,
    ],
  );

  // Months from FPI to enrollment target milestone (for chart annotation)
  const milestoneMonths = useMemo(() => {
    if (!fpiMilestone?.start_date || !enrollCompleteMilestone?.start_date)
      return null;
    const fpi = new Date(fpiMilestone.start_date);
    const ec = new Date(enrollCompleteMilestone.start_date);
    const months =
      (ec.getFullYear() - fpi.getFullYear()) * 12 +
      (ec.getMonth() - fpi.getMonth());
    return months > 0 ? months : null;
  }, [fpiMilestone?.start_date, enrollCompleteMilestone?.start_date]);

  if (!plan) return null;

  const hasSiteData = siteParams.some((s) => s.screenedPerMonth > 0);

  const save = (changes: Partial<typeof plan>) => updatePlan(plan.id, changes);

  const updateSite = (siteId: string, changes: Partial<PlannedSite>) => {
    save({
      sites: plan.sites.map((s) =>
        s.id === siteId ? { ...s, ...changes } : s,
      ),
    });
  };

  const updateScenario = (id: string, changes: Partial<EnrollmentScenario>) => {
    save({
      enrollmentScenarios: scenarios.map((s) =>
        s.id === id ? { ...s, ...changes } : s,
      ),
    });
  };

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Global settings strip */}
      <div className="flex items-center gap-3 mb-4 shrink-0 flex-wrap">
        <div className="flex items-center gap-2 rounded-[6px] bg-white border border-[#F0F2F4] px-3 py-2">
          <span className="text-[12px] text-[#6B7280]">Target enrollment</span>
          <InputNumber
            size="small"
            min={1}
            max={100000}
            value={targetEnrollment || undefined}
            placeholder="e.g. 120"
            onChange={(v) => save({ targetEnrollment: v ?? 0 })}
            style={{ width: 80 }}
          />
          <span className="text-[12px] text-[#6B7280]">patients</span>
        </div>
        <div className="flex items-center gap-2 rounded-[6px] bg-white border border-[#F0F2F4] px-3 py-2">
          <Tooltip title="Default screen failure rate applied to sites that don't have their own value set">
            <span className="text-[12px] text-[#6B7280] underline decoration-dotted cursor-help">
              Default SFR
            </span>
          </Tooltip>
          <InputNumber
            size="small"
            min={0}
            max={99}
            value={defaultScreenFailureRate}
            addonAfter="%"
            onChange={(v) => save({ screenFailureRate: v ?? 20 })}
            style={{ width: 80 }}
          />
        </div>
        {patientsToScreen > 0 && (
          <div className="flex items-center gap-1.5 rounded-[6px] bg-white border border-[#F0F2F4] px-3 py-2">
            <span className="text-[12px] text-[#6B7280]">To screen:</span>
            <span className="text-[13px] font-semibold text-[#1F2933]">
              {patientsToScreen}
            </span>
          </div>
        )}
        <div className="flex items-center gap-2 rounded-[6px] bg-white border border-[#F0F2F4] px-3 py-2">
          <Tooltip title="Months from site activation to reaching full enrollment speed">
            <span className="text-[12px] text-[#6B7280] underline decoration-dotted cursor-help">
              Ramp-up
            </span>
          </Tooltip>
          <InputNumber
            size="small"
            min={0}
            max={12}
            value={rampUpMonths}
            addonAfter="mo"
            onChange={(v) => save({ rampUpMonths: v ?? 2 })}
            style={{ width: 70 }}
          />
        </div>
        {fpiMilestone?.start_date && (
          <div className="flex items-center gap-1.5 rounded-[6px] bg-white border border-[#F0F2F4] px-3 py-2">
            <span className="text-[12px] text-[#6B7280]">FPI:</span>
            <span className="text-[13px] font-semibold text-[#1F2933]">
              {new Date(fpiMilestone.start_date).toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
                year: "numeric",
              })}
            </span>
          </div>
        )}
        {enrollCompleteMilestone?.start_date && (
          <div className="flex items-center gap-1.5 rounded-[6px] bg-[#FFFBEB] border border-[#FDE68A] px-3 py-2">
            <span className="text-[12px] text-[#92400E]">
              Enrollment target:
            </span>
            <span className="text-[13px] font-semibold text-[#92400E]">
              {new Date(enrollCompleteMilestone.start_date).toLocaleDateString(
                "en-US",
                { month: "short", day: "numeric", year: "numeric" },
              )}
            </span>
          </div>
        )}
      </div>

      {/* Two-panel layout */}
      <div className="flex gap-4 flex-1 min-h-0">
        {/* Left: Site Parameters */}
        <div className="flex flex-col" style={{ width: "66.67%" }}>
          <div className="bg-white rounded-[10px] border border-[#F0F2F4] flex flex-col overflow-hidden h-full">
            <div className="px-4 pt-4 pb-3 border-b border-[#F0F2F4] shrink-0">
              <h3 className="text-[15px] font-semibold text-[#1F2933] m-0">
                Site Enrollment Parameters
              </h3>
            </div>
            {plan.sites.length === 0 ? (
              <div className="flex flex-col items-center justify-center flex-1 px-6 text-center">
                <p className="text-[13px] text-[#6B7280]">
                  Add sites from the Investigators tab to set enrollment
                  parameters.
                </p>
              </div>
            ) : (
              <div className="overflow-auto flex-1">
                <table className="w-full text-left" style={{ minWidth: 500 }}>
                  <thead>
                    <tr className="bg-[#FAFAFA] border-b border-[#F0F2F4]">
                      <th className="px-3 py-2 text-[12px] font-semibold text-[#6B7280] uppercase tracking-wide">
                        Site
                      </th>
                      <th className="px-3 py-2 text-[12px] font-semibold text-[#6B7280] uppercase tracking-wide">
                        Location
                      </th>
                      <th className="px-3 py-2 text-[12px] font-semibold text-[#6B7280] uppercase tracking-wide">
                        <Tooltip title="Weeks after program SIV until this site begins enrolling">
                          <span className="underline decoration-dotted cursor-help">
                            Activation
                          </span>
                        </Tooltip>
                      </th>
                      <th className="px-3 py-2 text-[12px] font-semibold text-[#6B7280] uppercase tracking-wide">
                        <Tooltip title="Patients screened per month at this site">
                          <span className="underline decoration-dotted cursor-help">
                            Screened/mo
                          </span>
                        </Tooltip>
                      </th>
                      <th className="px-3 py-2 text-[12px] font-semibold text-[#6B7280] uppercase tracking-wide">
                        <Tooltip title="% of screened patients who fail eligibility. Blank = use plan default.">
                          <span className="underline decoration-dotted cursor-help">
                            Screen fail %
                          </span>
                        </Tooltip>
                      </th>
                      <th className="px-3 py-2 text-[12px] font-semibold text-[#6B7280] uppercase tracking-wide">
                        <Tooltip title="Max patients this site can enroll (0 = unlimited)">
                          <span className="underline decoration-dotted cursor-help">
                            Cap
                          </span>
                        </Tooltip>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {displaySites.map((site) => (
                      <SiteParamRow
                        key={site.id}
                        site={site}
                        onUpdate={(changes) => updateSite(site.id, changes)}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Right: Scenarios + Chart */}
        <div
          className="flex flex-col gap-3 min-h-0"
          style={{ width: "33.33%" }}
        >
          {/* Scenario multiplier cards */}
          <div
            className="bg-white rounded-[10px] border border-[#F0F2F4] p-4 overflow-auto"
            style={{ flex: 1 }}
          >
            <h3 className="text-[14px] font-semibold text-[#1F2933] mb-2 m-0">
              Scenario Multipliers
            </h3>
            <div className="flex flex-col gap-1.5">
              {scenarios.map((sc, idx) => {
                const col = CHART_COLORS[idx % CHART_COLORS.length];
                return (
                  <div
                    key={sc.id}
                    className={`rounded-[6px] border ${col.border} ${col.bg} px-3 py-2`}
                  >
                    <div className="flex items-center gap-1.5 mb-1.5">
                      <div
                        className="w-2 h-2 rounded-full shrink-0"
                        style={{ background: col.hex }}
                      />
                      <input
                        className={`text-[12px] font-semibold bg-transparent border-none outline-none flex-1 min-w-0 ${col.text}`}
                        value={sc.name}
                        onChange={(e) =>
                          updateScenario(sc.id, { name: e.target.value })
                        }
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="flex items-center gap-1 flex-1">
                        <span className="text-[11px] text-[#6B7280] shrink-0">
                          Rate
                        </span>
                        <InputNumber
                          size="small"
                          min={0.1}
                          max={3}
                          step={0.05}
                          precision={2}
                          value={sc.rateMultiplier}
                          addonAfter="×"
                          onChange={(v) =>
                            updateScenario(sc.id, { rateMultiplier: v ?? 1 })
                          }
                          style={{ width: "100%" }}
                        />
                      </div>
                      <div className="flex items-center gap-1 flex-1">
                        <Tooltip title="Multiplier on activation offset (< 1 = faster activation)">
                          <span className="text-[11px] text-[#6B7280] underline decoration-dotted cursor-help shrink-0">
                            Lag
                          </span>
                        </Tooltip>
                        <InputNumber
                          size="small"
                          min={0.1}
                          max={5}
                          step={0.05}
                          precision={2}
                          value={sc.activationLagMultiplier}
                          addonAfter="×"
                          onChange={(v) =>
                            updateScenario(sc.id, {
                              activationLagMultiplier: v ?? 1,
                            })
                          }
                          style={{ width: "100%" }}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Enrollment curve chart */}
          <div
            className="bg-white rounded-[10px] border border-[#F0F2F4] flex flex-col overflow-hidden"
            style={{ flex: 2 }}
          >
            <div className="px-4 pt-3 pb-2 border-b border-[#F0F2F4] shrink-0 flex items-center justify-between">
              <h3 className="text-[14px] font-semibold text-[#1F2933] m-0">
                Enrollment Projection
              </h3>
            </div>

            {!hasSiteData || targetEnrollment === 0 ? (
              <div className="flex flex-col items-center justify-center flex-1 px-6 text-center">
                <InfoCircleOutlined className="text-[24px] text-[#D1D5DB] mb-3" />
                <p className="text-[13px] text-[#6B7280]">
                  {targetEnrollment === 0
                    ? "Set a target enrollment to see projections."
                    : "Enter enrollment rates for at least one site to see projections."}
                </p>
              </div>
            ) : (
              <div className="flex flex-col flex-1 min-h-0">
                {/* Summary row */}
                <div className="grid grid-cols-3 gap-2 px-3 pt-3 pb-2 shrink-0">
                  {results.map((r, idx) => {
                    const col = CHART_COLORS[idx % CHART_COLORS.length];
                    return (
                      <div
                        key={r.scenario.id}
                        className={`rounded-[6px] border ${col.border} ${col.bg} px-2.5 py-2`}
                      >
                        <p
                          className={`text-[12px] font-semibold uppercase tracking-wide ${col.text} m-0`}
                        >
                          {r.scenario.name}
                        </p>
                        <p className="text-[15px] font-bold text-[#1F2933] mt-0.5 m-0">
                          {r.completionMonths != null
                            ? `${r.completionMonths} mo`
                            : "—"}
                        </p>
                        {r.projectedDate ? (
                          <p
                            className={`text-[12px] mt-0.5 m-0 flex items-center gap-1 ${
                              r.isLate === true
                                ? "text-[#991B1B]"
                                : r.isLate === false
                                  ? "text-[#065F46]"
                                  : "text-[#6B7280]"
                            }`}
                          >
                            {r.isLate === true && <WarningOutlined />}
                            {r.isLate === false && <CheckCircleOutlined />}
                            {r.projectedDate}
                          </p>
                        ) : (
                          <p className="text-[12px] text-[#6B7280] mt-0.5 m-0">
                            Set FPI milestone
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Chart */}
                <div className="px-2 pb-2 flex-1 min-h-0">
                  <EnrollmentChart
                    results={results}
                    target={targetEnrollment}
                    milestoneMonth={milestoneMonths}
                    fpiDate={fpiMilestone?.start_date ?? null}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
